import { LayoutSizeContext } from 'App';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import WeatherWidgetDetails from 'features/Weather/components/WeatherWidgetDetails/WeatherWidgetDetails';
import WeatherWidgetSelector from 'features/Weather/components/WeatherWidgetSelector/WeatherWidgetSelector';
import useLocation from 'hooks/useLocation';
import { useContext, useMemo, useState } from 'react';
import { GiBuoy } from 'react-icons/gi';
import { useGetBuoyConditionsQuery } from 'services/weather/marine/marine.api';
import { joinClasses } from 'utils/componentStyles/style.utils';
import MarineWidgetSelect from '../../components/MarineWidgetSelect/MarineWidgetSelect';
import {
  getBuoyConditionLocations,
  getBuoyObservationCardKey,
} from '../../features/BuoyConditions/utils/buoyConditions.utils';
import {
  getActiveMarineWidgetItem,
  getMarineWidgetActiveItemIndex,
  getMarineWidgetErrorMessage,
} from '../../utils/marineForecastWidget.utils';
import './assets/buoyConditionsWidget.scss';
import { details, layoutConfig, route } from './config/buoyConditionsWidget.config';
import {
  getBuoyConditionsWidgetLayout,
  getBuoyConditionsWidgetLocationOptions,
  getBuoyConditionsWidgetObservationDateLabel,
  getBuoyConditionsWidgetObservationTimeLabel,
  getBuoyConditionsWidgetTileAriaLabel,
} from './utils/buoyConditionsWidget.utils';

const BuoyConditionsWidget = ({ layout = '1x1', dimensions, containerSizePx }) => {
  const layoutSize = useContext(LayoutSizeContext);
  const { locations } = useLocation();
  const {
    data: buoyConditions = [],
    isLoading: isLoadingBuoyConditions,
    isFetching: isFetchingBuoyConditions,
    isError: isBuoyConditionsError,
    error: buoyConditionsError,
    refetch: refetchBuoyConditions,
    fulfilledTimeStamp,
  } = useGetBuoyConditionsQuery(locations.effectiveLocation);
  const [selectedLocationKey, setSelectedLocationKey] = useState(null);
  const [selectedObservationIndex, setSelectedObservationIndex] = useState(0);

  const buoyLocations = useMemo(() => getBuoyConditionLocations(buoyConditions), [buoyConditions]);

  const locationOptions = useMemo(
    () => getBuoyConditionsWidgetLocationOptions(buoyLocations),
    [buoyLocations],
  );

  const activeLocation = getActiveMarineWidgetItem(
    buoyLocations,
    selectedLocationKey,
    ({ locationKey }) => locationKey,
  );

  const observations = activeLocation?.observations ?? [];

  const activeObservationIndex = getMarineWidgetActiveItemIndex(
    observations,
    selectedObservationIndex,
  );

  const selectedObservation = observations[activeObservationIndex];
  const config = layoutConfig[layout] ?? layoutConfig['1x1'];
  const widgetLayout = getBuoyConditionsWidgetLayout({
    containerSizePx,
    layoutSize,
    dimensions,
  });
  const isRefreshing = isFetchingBuoyConditions && !isLoadingBuoyConditions;

  if (isBuoyConditionsError && !isLoadingBuoyConditions) {
    return (
      <ErrorMessage
        message={getMarineWidgetErrorMessage(
          buoyConditionsError,
          'Unable to load buoy conditions.',
        )}
        retryRoute={route}
        onRetry={() => refetchBuoyConditions()}
      />
    );
  }

  if (isLoadingBuoyConditions) {
    return <WidgetLoadingAnimation widgetName="BuoyCondition" dimensions={dimensions} />;
  }

  if (!buoyLocations.length || !observations.length) {
    return (
      <section aria-label="Buoy conditions widget">
        <WidgetTimeUpdate
          route={route}
          compact={config.compactTimeUpdate}
          updatedAt={fulfilledTimeStamp ?? null}
        />
        <p className="p-3 mb-0" role="status">
          No buoy condition data found.
        </p>
      </section>
    );
  }

  return (
    <section
      aria-label="Buoy conditions widget"
      aria-busy={isRefreshing || undefined}
      className={joinClasses('buoy-conditions-widget', config.wrapperClassName)}
      data-layout={layout}
    >
      <WidgetTimeUpdate
        route={route}
        compact={config.compactTimeUpdate}
        updatedAt={fulfilledTimeStamp ?? null}
      />
      {isRefreshing && (
        <span className="visually-hidden" role="status">
          Updating buoy conditions.
        </span>
      )}

      <div className={config.contentClassName}>
        <div className={config.controlsClassName}>
          <MarineWidgetSelect
            label="Buoy station"
            options={locationOptions}
            selectedValue={activeLocation?.locationKey}
            placeholder="Select a buoy station"
            isSearchable
            onSelect={(locationKey) => {
              setSelectedLocationKey(locationKey ?? null);
              setSelectedObservationIndex(0);
            }}
          />
        </div>

        <div
          className="pt-2"
          style={{
            flex: `0 0 ${config.selectorHeight}`,
            height: config.selectorHeight,
            minHeight: 0,
          }}
        >
          <WeatherWidgetSelector
            items={observations}
            selectedIndex={activeObservationIndex}
            visibleNumOfItems={config.visibleCount(widgetLayout)}
            selectorMode={config.selectorMode}
            gridColumnCount={config.gridColumnCount}
            gap={config.selectorGap}
            arrowButtonWidth={widgetLayout.arrowButtonWidth}
            arrowIconSize={widgetLayout.arrowIconSize}
            listContainerStyle={config.selectorListStyle}
            tileStyle={config.selectorTileStyle}
            ariaLabel="Buoy condition observations"
            previousButtonLabel="Show previous buoy observation"
            nextButtonLabel="Show next buoy observation"
            onSelect={setSelectedObservationIndex}
            getKey={getBuoyObservationCardKey}
            getTileProps={(entry) => ({
              label: getBuoyConditionsWidgetObservationDateLabel(entry),
              value: getBuoyConditionsWidgetObservationTimeLabel(entry),
              ariaLabel: getBuoyConditionsWidgetTileAriaLabel(entry),
              icon: (
                <span
                  aria-hidden="true"
                  className="buoy-conditions-widget-tile-icon d-flex h-100 w-100 align-items-center justify-content-center"
                >
                  <GiBuoy />
                </span>
              ),
              iconStyle: {
                width:
                  config.selectorMode === 'row'
                    ? widgetLayout.rowTileIconSize
                    : widgetLayout.tileIconSize,
                height:
                  config.selectorMode === 'row'
                    ? widgetLayout.rowTileIconSize
                    : widgetLayout.tileIconSize,
              },
              labelStyle: widgetLayout.tileTextStyle,
              valueStyle: widgetLayout.tileTextStyle,
            })}
          />
        </div>

        <WeatherWidgetDetails
          selectedItem={selectedObservation}
          details={details}
          layoutGroups={config.detailGroups}
          containerClassName={config.detailsClassName}
          containerStyle={config.detailsStyle({ layoutSize })}
          iconSize={config.detailsIconSize}
          ariaLabel={`Buoy conditions for ${activeLocation?.locationName ?? 'selected station'}, ${getBuoyConditionsWidgetObservationDateLabel(
            selectedObservation,
          )} ${getBuoyConditionsWidgetObservationTimeLabel(selectedObservation)}`}
        />
      </div>
    </section>
  );
};

export default BuoyConditionsWidget;
